import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import { Denuncia, EstatusDenuncia } from 'src/app/shared/model/denuncias/denuncia.model';
import { DenunciaComentario } from 'src/app/shared/model/denuncias/denuncia-comentario.model';
import { DenunciaEvidencia } from 'src/app/shared/model/denuncias/denuncia-evidencia.model';
import { DenunciaHistorial } from 'src/app/shared/model/denuncias/denuncia-historial.model';
import { DenunciasService } from '../denuncias.service';
import { EmpleadoService } from 'src/app/empleado/empleado.service';
import { Empleado } from 'src/app/shared/model/empleado/empleado.model';
import { EVIDENCIA_MIME, extensionDe, tipoVisorDe } from '../evidencia-permitida';

// Sin 'texto': un .txt/.html/.svg ya no es evidencia valida y nunca se pinta.
type TipoVisor = 'imagen' | 'pdf' | 'otro';

@Component({
  selector: 'app-detalle-denuncia',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './detalle-denuncia.component.html',
  styleUrls: ['./detalle-denuncia.component.css']
})
export class DetalleDenunciaComponent implements OnInit, OnDestroy {

  denuncia: Denuncia = new Denuncia();
  historial: DenunciaHistorial[] = [];
  comentarios: DenunciaComentario[] = [];
  evidencias: DenunciaEvidencia[] = [];
  nuevoComentario: string = '';

  estatusList = [
    { id: EstatusDenuncia.RECIBIDA,         nombre: 'Recibida' },
    { id: EstatusDenuncia.EN_INVESTIGACION, nombre: 'En Investigación' },
    { id: EstatusDenuncia.CERRADA,          nombre: 'Cerrada' }
  ];

  // Mapa id_evidencia → SafeUrl (base64 cargada)
  evidenciaImgs: Map<number, SafeUrl> = new Map();
  evidenciaObjectUrls: Map<number, string> = new Map();
  evidenciasCargando: Set<number> = new Set();
  evidenciasFallidas: Set<number> = new Set();

  // Lightbox
  lightboxSrc: SafeUrl | null = null;
  lightboxNombre: string = '';

  cargando: boolean = true;
  guardando: boolean = false;
  enviandoComentario: boolean = false;
  mensajeExito: string = '';
  mensajeError: string = '';

  // Una denuncia Cerrada no se puede reabrir ni modificar su estado.
  denunciaCerrada: boolean = false;

  // Investigador asignado (catálogo de empleados activos)
  investigadores: Empleado[] = [];
  idInvestigadorSel: number = null;

  // Acciones de documento
  exportando: boolean = false;
  descargando: boolean = false;

  // Visor de evidencias (modal): imagen / pdf / texto / otro
  visorAbierto: boolean = false;
  visorTipo: TipoVisor = 'otro';
  visorUrl: SafeUrl | SafeResourceUrl | null = null;
  visorObjectUrl: string | null = null;
  visorNombre: string = '';
  visorEvidencia: DenunciaEvidencia = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private denunciasService: DenunciasService,
    private empleadoService: EmpleadoService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarInvestigadores();
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.cargarDetalle(+id);
      }
    });
  }

  cargarInvestigadores(): void {
    this.empleadoService.consultaEmpleadosActivos().subscribe({
      next: resp => {
        this.investigadores = (resp?.list || []).filter(e => this.idUsuarioEmpleado(e) != null);
        this.cdr.detectChanges();
      },
      error: () => { this.investigadores = []; }
    });
  }

  /** Id de USUARIO del empleado (lo que referencia id_usuario_asignado). */
  idUsuarioEmpleado(emp: Empleado): number {
    return emp?.usuario?.id ?? emp?.idUsuario ?? null;
  }

  /** Nombre legible del empleado para el selector. */
  nombreEmpleado(emp: Empleado): string {
    const p = emp?.persona;
    if (!p) { return 'Usuario ' + (this.idUsuarioEmpleado(emp) ?? ''); }
    if (p.nombreCompleto) { return p.nombreCompleto; }
    return [p.nombre, p.apellidoPaterno, p.apellidoMaterno].filter(Boolean).join(' ').trim()
      || ('Usuario ' + (this.idUsuarioEmpleado(emp) ?? ''));
  }

  ngOnDestroy(): void {
    this.limpiarImagenesEvidencia();
  }

  cargarDetalle(id: number): void {
    this.cargando = true;
    this.mensajeError = '';
    this.limpiarImagenesEvidencia();
    this.evidencias = [];
    const req = new Denuncia();
    req.id_denuncia = id;

    this.denunciasService.consultarDetalle(req).pipe(timeout(20000)).subscribe({
      next: resp => {
        this.cargando = false;
        if (resp?.pCodRetorno === '0') {
          this.denuncia = resp;
          this.denunciaCerrada = this.denuncia.id_estatus === EstatusDenuncia.CERRADA;
          this.idInvestigadorSel = this.denuncia.id_usuarioAsignado || null;
          if (resp['historial'])   { this.historial   = resp['historial'];   }
          else                     { this.cargarHistorial(); }
          if (resp['comentarios']) { this.comentarios = resp['comentarios']; }
          if (resp['evidencias'])  {
            this.evidencias = resp['evidencias'];
            this.cargarImagenesEvidencia();
          }
        } else {
          this.mensajeError = resp?.pMensaje || 'No se pudo cargar el detalle de la denuncia.';
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.mensajeError = 'Error al conectar con el servidor. Intente nuevamente.';
        this.cdr.detectChanges();
      }
    });
  }

  cargarHistorial(): void {
    const req = new Denuncia();
    req.id_denuncia = this.denuncia.id_denuncia;
    this.denunciasService.consultarHistorial(req).subscribe(resp => {
      if (resp?.list) {
        this.historial = resp.list;
        this.cdr.detectChanges();
      }
    });
  }

  // ── Evidencias ────────────────────────────────────────────────────────────

  /**
   * La extension que valido el servidor (o la del nombre en renglones
   * viejos). Nunca el tipoArchivo que declaro el navegador del denunciante.
   */
  private extensionEvidencia(ev: DenunciaEvidencia): string {
    return ((ev as DenunciaEvidencia & { extension?: string }).extension || extensionDe(ev.nombreArchivo)).toLowerCase();
  }

  esImagen(ev: DenunciaEvidencia): boolean {
    return tipoVisorDe(this.extensionEvidencia(ev)) === 'imagen';
  }

  /**
   * El blob se vuelve a tipar con la lista blanca: aunque el servidor ya
   * manda el tipo correcto, el visor no depende de el. Un tipo fuera de la
   * lista nunca se pinta: queda como octet-stream.
   */
  private blobSeguro(ev: DenunciaEvidencia, blob: Blob): Blob {
    const tipo = EVIDENCIA_MIME[this.extensionEvidencia(ev)] || 'application/octet-stream';
    return new Blob([blob], { type: tipo });
  }

  cargarImagenesEvidencia(): void {
    this.evidencias.forEach(ev => {
      if (this.esImagen(ev)) {
        this.cargarImagen(ev);
      }
    });
  }

  cargarImagen(ev: DenunciaEvidencia): void {
    const id = ev.id_evidencia;
    if (this.evidenciaImgs.has(id) || this.evidenciasCargando.has(id) || this.evidenciasFallidas.has(id)) {
      return;
    }
    this.evidenciasCargando.add(id);
    this.cdr.detectChanges();

    this.denunciasService.consultarEvidenciaImg(ev).subscribe({
      next: blob => {
        if (!blob || blob.size === 0) {
          this.evidenciasFallidas.add(id);
        } else {
          const objectUrl = URL.createObjectURL(this.blobSeguro(ev, blob));
          const src = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
          this.evidenciaObjectUrls.set(id, objectUrl);
          this.evidenciaImgs.set(id, src);
        }
        this.evidenciasCargando.delete(id);
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: () => {
        this.evidenciasFallidas.add(id);
        this.evidenciasCargando.delete(id);
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  // ── Visor de evidencias (modal): imagen / pdf / otro ─────────────────────────
  private tipoEvidencia(ev: DenunciaEvidencia): TipoVisor {
    return tipoVisorDe(this.extensionEvidencia(ev));
  }

  verEvidencia(ev: DenunciaEvidencia): void {
    this.cerrarVisor();
    this.visorNombre = ev.nombreArchivo;
    this.visorEvidencia = ev;
    this.visorTipo = this.tipoEvidencia(ev);

    // Word, Excel y lo que no esta en la lista no se abren en el navegador:
    // el visor solo ofrece descargarlos.
    if (this.visorTipo === 'otro') {
      this.visorAbierto = true;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      return;
    }

    this.denunciasService.consultarEvidenciaImg(ev).subscribe({
      next: blob => {
        if (!blob || blob.size === 0) {
          this.mensajeError = 'No se pudo abrir la evidencia.';
          this.cdr.markForCheck();
          this.cdr.detectChanges();
          return;
        }
        const objectUrl = URL.createObjectURL(this.blobSeguro(ev, blob));
        this.visorObjectUrl = objectUrl;
        this.visorUrl = this.visorTipo === 'imagen'
          ? this.sanitizer.bypassSecurityTrustUrl(objectUrl)
          : this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
        this.visorAbierto = true;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: () => {
        this.mensajeError = 'No se pudo abrir la evidencia.';
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Chrome y Edge pueden negarse a mostrar su visor de PDF dentro de un
   * iframe con sandbox; el PDF (blob ya tipado como application/pdf) se
   * abre entonces en otra pestaña, fuera de la pagina del COS.
   */
  abrirEnPestana(): void {
    if (this.visorObjectUrl && this.visorTipo === 'pdf') {
      window.open(this.visorObjectUrl, '_blank', 'noopener');
    }
  }

  cerrarVisor(): void {
    if (this.visorObjectUrl) { URL.revokeObjectURL(this.visorObjectUrl); this.visorObjectUrl = null; }
    this.visorAbierto = false;
    this.visorUrl = null;
  }

  // ── Descargas ────────────────────────────────────────────────────────────────
  descargarEvidencia(ev: DenunciaEvidencia): void {
    this.denunciasService.consultarEvidenciaImg(ev).subscribe({
      next: blob => {
        if (blob && blob.size) {
          saveAs(blob, ev.nombreArchivo || ('evidencia-' + ev.id_evidencia));
        } else {
          this.mensajeError = 'No se pudo descargar la evidencia.';
          this.cdr.detectChanges();
        }
      },
      error: () => { this.mensajeError = 'No se pudo descargar la evidencia.'; this.cdr.detectChanges(); }
    });
  }

  async descargarTodo(): Promise<void> {
    if (!this.evidencias.length || this.descargando) { return; }
    this.descargando = true;
    this.mensajeError = '';
    this.cdr.detectChanges();
    let ok = 0;
    for (const ev of this.evidencias) {
      try {
        const blob = await firstValueFrom(this.denunciasService.consultarEvidenciaImg(ev));
        if (blob && blob.size) {
          saveAs(blob, ev.nombreArchivo || ('evidencia-' + ev.id_evidencia));
          ok++;
          // Pequeña pausa para que el navegador procese cada descarga.
          await new Promise(resolve => setTimeout(resolve, 350));
        }
      } catch {
        // Continuar con las demás evidencias aunque una falle.
      }
    }
    this.descargando = false;
    if (ok < this.evidencias.length) {
      this.mensajeError = `Se descargaron ${ok} de ${this.evidencias.length} evidencias; el resto no está disponible en el servidor.`;
    }
    this.cdr.detectChanges();
  }

  private fmtFechaPdf(v: any, conHora = false): string {
    if (!v) { return '—'; }
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) { return String(v); }
    const opts: Intl.DateTimeFormatOptions = conHora
      ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: 'short', year: 'numeric' };
    return d.toLocaleString('es-MX', opts);
  }

  // ── Exportar PDF: documento real por secciones (texto, no captura de pantalla) ──
  exportarPdf(): void {
    if (this.exportando) { return; }
    this.exportando = true;
    this.mensajeError = '';
    this.cdr.detectChanges();
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const mL = 18, mR = 18, mT = 22, mB = 18;
      const contentW = pageW - mL - mR;
      let y = mT;

      const ink = () => pdf.setTextColor(33, 37, 41);
      const muted = () => pdf.setTextColor(110, 116, 128);
      const ensure = (h: number) => { if (y + h > pageH - mB) { pdf.addPage(); y = mT; } };

      // Banda de sección
      const titulo = (txt: string) => {
        ensure(15);
        const alto = 8.5;
        pdf.setFillColor(247, 249, 251);
        pdf.rect(mL, y, contentW, alto, 'F');
        pdf.setFillColor(183, 28, 28);
        pdf.rect(mL, y, 1.8, alto, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10.5);
        pdf.setTextColor(183, 28, 28);
        pdf.text((txt || '').toUpperCase(), mL + 5.5, y + 5.6);
        y += alto + 4.5;
      };
      const par = (txt: string) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        ink();
        const lines = pdf.splitTextToSize(txt && txt.trim() ? txt : '—', contentW - 4);
        for (const ln of lines) { ensure(5.4); pdf.text(ln, mL + 2, y + 3.7); y += 5.3; }
        y += 2.5;
      };
      const fila = (label: string, valor: string) => {
        const labelW = 54;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9.5);
        const valLines = pdf.splitTextToSize(valor && valor.trim() ? valor : '—', contentW - labelW - 4);
        const alto = Math.max(7, valLines.length * 5 + 2);
        ensure(alto);
        muted(); pdf.setFont('helvetica', 'bold');
        pdf.text(label, mL + 2, y + 4.2);
        ink(); pdf.setFont('helvetica', 'normal');
        let yy = y;
        for (const ln of valLines) { pdf.text(ln, mL + labelW, yy + 4.2); yy += 5; }
        pdf.setDrawColor(235, 238, 242); pdf.setLineWidth(0.2);
        pdf.line(mL + 2, y + alto - 1, pageW - mR - 2, y + alto - 1);
        y += alto;
      };

      // Membrete (banda superior tipo encabezado formal)
      pdf.setFillColor(183, 28, 28);
      pdf.rect(0, 0, pageW, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
      pdf.text('SOLSER · LÍNEA ÉTICA', mL, 7.8);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
      pdf.text('DOCUMENTO CONFIDENCIAL', pageW - mR, 7.8, { align: 'right' });

      // Título centrado
      ink(); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(20);
      pdf.text('Reporte de Denuncia', pageW / 2, y + 2, { align: 'center' }); y += 9;

      // Folio destacado y centrado
      muted(); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
      pdf.text('FOLIO DE SEGUIMIENTO', pageW / 2, y, { align: 'center' }); y += 5.5;
      pdf.setTextColor(183, 28, 28); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
      pdf.text(this.denuncia.codigoSeguimiento || '—', pageW / 2, y, { align: 'center' }); y += 7;

      // Meta centrada
      const tipo = this.denuncia.tipoDenuncia === 'ANONIMA' ? 'Anónima' : 'Confidencial';
      muted(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
      pdf.text(
        `Estado: ${this.denuncia.nombreEstatus || '—'}      ·      Tipo: ${tipo}      ·      Registro: ${this.fmtFechaPdf(this.denuncia.fechaRegistro)}`,
        pageW / 2, y, { align: 'center' }
      ); y += 8;
      pdf.setDrawColor(224, 228, 234); pdf.setLineWidth(0.4); pdf.line(mL, y, pageW - mR, y); y += 8;

      // Hechos
      titulo('Hechos reportados');
      par(this.denuncia.hechos || 'Sin descripción registrada.');

      // Información de seguridad
      titulo('Información de seguridad');
      fila('Anónimo:', this.denuncia.tipoDenuncia === 'ANONIMA' ? 'Sí' : 'No');
      fila('Requiere protección:', this.denuncia.requiereProteccion || 'No');
      if (this.denuncia.tipoDenuncia === 'CONFIDENCIAL') {
        const nombre = `${this.denuncia.nombre || ''} ${this.denuncia.apellidos || ''}`.trim();
        fila('Denunciante:', nombre || '—');
        if (this.denuncia.telefono) { fila('Teléfono:', this.denuncia.telefono); }
        if (this.denuncia.email) { fila('Correo:', this.denuncia.email); }
      }
      y += 2;

      // Información del registro
      titulo('Información del registro');
      fila('Fecha de envío:', this.fmtFechaPdf(this.denuncia.fechaRegistro));
      fila('Categoría:', this.denuncia.nombreCategoria || 'Sin categoría');
      fila('Canal de reporte:', 'Línea Ética Web');
      if (this.denuncia.lugarOcurrencia) { fila('Lugar:', this.denuncia.lugarOcurrencia); }
      y += 2;

      // Gestión
      titulo('Gestión del caso');
      fila('Estado actual:', this.denuncia.nombreEstatus || '—');
      fila('Investigador asignado:', this.denuncia.nombreUsuarioAsignado || 'Sin asignar');
      y += 2;

      // Evidencias
      titulo(`Evidencias adjuntas (${this.evidencias.length})`);
      if (!this.evidencias.length) { par('Sin evidencias adjuntas.'); }
      else { for (const ev of this.evidencias) { par(`•  ${ev.nombreArchivo || 'evidencia'}   (${this.formatBytes(ev.tamanio)})`); } }

      // Comentarios
      titulo(`Comentarios internos (${this.comentarios.length})`);
      if (!this.comentarios.length) { par('Sin comentarios internos.'); }
      else {
        for (const c of this.comentarios) {
          ensure(5); muted(); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5);
          pdf.text(`${c.nombreUsuario || 'Usuario'} · ${this.fmtFechaPdf(c.fecha, true)}`, mL, y + 3.5); y += 5;
          par(c.comentario || '—');
        }
      }

      // Historial
      titulo('Historial de actividad');
      if (!this.historial.length) { par('Sin cambios registrados.'); }
      else {
        for (const h of this.historial) {
          ensure(5.4); ink(); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
          pdf.text(h.nombreEstatusNuevo || '—', mL, y + 3.5); y += 5;
          ensure(5); muted(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
          pdf.text(`${h.nombreUsuario || ''} · ${this.fmtFechaPdf(h.fecha, true)}`, mL, y + 3.3); y += 5;
          if (h.observacion) { par(h.observacion); } else { y += 1.5; }
        }
      }

      // Pie de página en todas las hojas
      const total = pdf.getNumberOfPages();
      const generado = this.fmtFechaPdf(new Date(), true);
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(224, 228, 234); pdf.setLineWidth(0.3);
        pdf.line(mL, pageH - 13, pageW - mR, pageH - 13);
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); muted();
        pdf.text('Solser · Línea Ética — Documento confidencial', mL, pageH - 8);
        pdf.text(`Generado ${generado}   ·   Página ${i} de ${total}`, pageW - mR, pageH - 8, { align: 'right' });
      }

      pdf.save(`Denuncia-${this.denuncia.codigoSeguimiento || this.denuncia.id_denuncia}.pdf`);
    } catch {
      this.mensajeError = 'No se pudo exportar el PDF.';
    } finally {
      this.exportando = false;
      this.cdr.detectChanges();
    }
  }

  formatBytes(bytes: number): string {
    if (!bytes) { return ''; }
    if (bytes < 1024)       { return bytes + ' B'; }
    if (bytes < 1048576)    { return (bytes / 1024).toFixed(1) + ' KB'; }
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // ── Gestión ───────────────────────────────────────────────────────────────

  guardarCambios(): void {
    if (this.denunciaCerrada) {
      this.mensajeError = 'La denuncia está cerrada: no se puede reabrir ni cambiar su estado.';
      return;
    }

    this.guardando = true;
    this.mensajeExito = '';
    this.mensajeError = '';

    this.denunciasService.actualizarEstatus(this.denuncia).subscribe({
      next: resp => {
        if (resp?.pCodRetorno === '0') {
          this.persistirInvestigador();
        } else {
          this.guardando = false;
          this.mensajeError = resp?.pMensaje || 'Error al guardar cambios.';
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.guardando = false;
        this.mensajeError = 'Error de conexión.';
        this.cdr.detectChanges();
      }
    });
  }

  /** actualizarEstatus NO guarda la asignación; el investigador se persiste aparte. */
  private persistirInvestigador(): void {
    const idActual = this.denuncia.id_usuarioAsignado || null;
    if (this.idInvestigadorSel == null || this.idInvestigadorSel === idActual) {
      this.finalizarGuardado();
      return;
    }
    const req = new Denuncia();
    req.id_denuncia = this.denuncia.id_denuncia;
    req.id_usuarioAsignado = this.idInvestigadorSel;
    this.denunciasService.asignarUsuario(req).subscribe({
      next: resp => {
        // El servidor solo asigna a gente de la misma empresa: si lo niega,
        // no se pinta como guardado.
        if (resp?.pCodRetorno && resp.pCodRetorno !== '0') {
          this.guardando = false;
          this.mensajeError = 'Estatus guardado, pero no se pudo asignar el investigador: '
            + (resp.pMensaje || 'intente de nuevo.');
          this.cdr.markForCheck();
          this.cdr.detectChanges();
          return;
        }
        this.finalizarGuardado();
      },
      error: () => {
        this.guardando = false;
        this.mensajeError = 'Estatus guardado, pero no se pudo asignar el investigador.';
        this.cdr.detectChanges();
      }
    });
  }

  private finalizarGuardado(): void {
    this.guardando = false;
    this.mensajeExito = 'Cambios guardados correctamente.';
    this.denunciaCerrada = this.denuncia.id_estatus === EstatusDenuncia.CERRADA;
    const emp = this.investigadores.find(e => this.idUsuarioEmpleado(e) === this.idInvestigadorSel);
    if (emp) {
      this.denuncia.id_usuarioAsignado = this.idInvestigadorSel;
      this.denuncia.nombreUsuarioAsignado = this.nombreEmpleado(emp);
    }
    this.cargarHistorial();
    setTimeout(() => { this.mensajeExito = ''; this.cdr.detectChanges(); }, 3000);
    this.cdr.detectChanges();
  }

  agregarComentario(): void {
    if (!this.nuevoComentario.trim()) { return; }
    this.enviandoComentario = true;

    const comentario = new DenunciaComentario();
    comentario.id_denuncia = this.denuncia.id_denuncia;
    comentario.comentario   = this.nuevoComentario.trim();

    this.denunciasService.guardarComentario(comentario).subscribe({
      next: resp => {
        this.enviandoComentario = false;
        if (resp?.pCodRetorno === '0') {
          this.comentarios.unshift(resp);
          this.nuevoComentario = '';
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.enviandoComentario = false;
        this.cdr.detectChanges();
      }
    });
  }

  volver(): void {
    this.router.navigate(['/DENUNCIAS/kanban']);
  }

  getBadgeClass(id_estatus: number): string {
    const map: Record<number, string> = {
      1: 'det-badge--recibida',
      2: 'det-badge--investigacion',
      3: 'det-badge--cerrada'
    };
    return map[id_estatus] || '';
  }

  getInitials(nombre: string): string {
    if (!nombre) { return '?'; }
    return nombre.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  }

  private limpiarImagenesEvidencia(): void {
    this.evidenciaObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.evidenciaObjectUrls.clear();
    this.evidenciaImgs.clear();
    this.evidenciasCargando.clear();
    this.evidenciasFallidas.clear();
    this.cerrarVisor();
  }
}
